import { useMemo } from "react";
import { useGetCashFlowTimeSeriesQuery } from "@/features/accounting/financial-reports/api";
import type { CashFlowChartPoint } from "../types/api";
import { parseMoney } from "../utils/format";
import { formatChartLabel } from "../utils/chartLabels";

interface UseCashFlowDataParams {
  startDate: string;
  endDate: string;
  interval: string | undefined;
  projectId: number | null | undefined;
  locale: string;
}

interface CashFlowResult {
  chartData: CashFlowChartPoint[];
  totalInflows: number;
  totalOutflows: number;
  netCashChange: number;
  isAvailable: boolean;
  isLoading: boolean;
}

export function useCashFlowData({
  startDate,
  endDate,
  interval,
  projectId,
  locale,
}: UseCashFlowDataParams): CashFlowResult {
  const canFetch = Boolean(startDate && endDate && interval);

  const {
    data: cashFlowTimeSeries,
    isLoading,
    isError,
  } = useGetCashFlowTimeSeriesQuery(
    {
      start_date: startDate,
      end_date: endDate,
      interval: interval ?? "month",
      project_id: projectId ?? undefined,
    },
    { skip: !canFetch },
  );

  return useMemo<CashFlowResult>(function buildResult() {
    const hasData = (cashFlowTimeSeries?.points?.length ?? 0) > 0;

    if (!hasData) {
      return {
        chartData: [],
        totalInflows: 0,
        totalOutflows: 0,
        netCashChange: 0,
        isAvailable: canFetch && isLoading && !isError,
        isLoading,
      };
    }

    const chartData = parseCashFlowPoints(
      cashFlowTimeSeries!.points,
      (interval ?? "month") as "day" | "week" | "month" | "quarter" | "half_year" | "halfyear",
      locale,
    );

    let totalInflows = 0;
    let totalOutflows = 0;
    for (const point of cashFlowTimeSeries!.points) {
      totalInflows += parseMoney(point.inflows_total);
      totalOutflows += parseMoney(point.outflows_total);
    }

    return {
      chartData,
      totalInflows,
      totalOutflows,
      netCashChange: totalInflows - totalOutflows,
      isAvailable: true,
      isLoading: false,
    };
  }, [cashFlowTimeSeries, canFetch, isLoading, isError, interval, locale]);
}

export function parseCashFlowPoints(
  points: { date: string; inflows_total: string; outflows_total: string; closing_cash: string }[],
  interval: "day" | "week" | "month" | "quarter" | "half_year" | "halfyear",
  locale: string,
): CashFlowChartPoint[] {
  return points.map(function buildPoint(point) {
    const inflows = parseMoney(point.inflows_total);
    const outflows = parseMoney(point.outflows_total);
    return {
      label: formatChartLabel(point.date, interval, locale),
      date: point.date,
      inflows,
      outflows: -outflows,
      netChange: inflows - outflows,
    };
  });
}
